import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { formatMoney, template } from '@belong/common';
import { Text } from '@belong/ui';
import { useSplitTreatments } from '@splitsoftware/splitio-react';
import { STRINGS } from 'pages/PostInspectionFlow/steps/Improvements/strings';
import { fetchMissingProServicesUponLeaseExecution } from 'store/redux/maintenance/actions';

type Props = {
  bundleList: {
    items: {
      maintenance: {
        summary: string;
      };
    }[];
  }[];
  reportJobId: string;
};

// Service name mappings for cleaning and security services
const CLEANING_PRO_SERVICE = {
  preMoveInSpotClean: 'Belong Surface Clean',
  preMoveInDeepClean: 'Belong Signature Home Detailing',
} as const;

const SECURITY_SERVICES = [
  'Rekeying and Key Copying',
  'Keyless Entry Recoding',
];

const PRE_MOVE_IN_CLEANING_PRO_SERVICES_NAMES: string[] = [
  CLEANING_PRO_SERVICE.preMoveInSpotClean,
  CLEANING_PRO_SERVICE.preMoveInDeepClean,
];

export function RekeyingPreMoveInSpotCleanDisclaimer({
  bundleList,
  reportJobId,
}: Props) {
  const { treatments, isReady } = useSplitTreatments({
    names: ['automatic-pre-move-in-repairs'],
  });
  const dispatch = useDispatch();
  const [missingProServices, setMissingProServices] = useState<Record<
    string,
    number
  > | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  // Fetch missing pro services when component mounts
  useEffect(() => {
    if (
      reportJobId &&
      isReady &&
      treatments['automatic-pre-move-in-repairs']!.treatment !== 'off'
    ) {
      setIsLoading(true);
      const fetchServices = async () => {
        try {
          const response = await dispatch(
            fetchMissingProServicesUponLeaseExecution(reportJobId),
          );
          setMissingProServices(response);
        } catch (error) {
          console.error('Error fetching missing pro services:', error);
          setMissingProServices(null);
        } finally {
          setIsLoading(false);
        }
      };
      fetchServices();
    }
  }, [reportJobId, isReady, treatments, dispatch]);

  if (!isReady) {
    return null;
  }

  // Asserted: this flag is always present in the treatments map, and the guard
  // above already dereferenced it the same way.
  const { treatment } = treatments['automatic-pre-move-in-repairs']!;

  if (treatment === 'off') {
    return null;
  }

  if (isLoading || !missingProServices) {
    return null;
  }

  // Get service names and prices from the API response
  const serviceNames = Object.keys(missingProServices);
  const cleaningService = serviceNames.find((name) =>
    PRE_MOVE_IN_CLEANING_PRO_SERVICES_NAMES.includes(name),
  );
  const securityService = serviceNames.find((name) =>
    SECURITY_SERVICES.includes(name),
  );

  // Both keys come from `Object.keys(missingProServices)` immediately above, so
  // the lookups cannot miss.
  const cleaningPrice = cleaningService
    ? missingProServices[cleaningService]!
    : 0;
  const securityPrice = securityService
    ? missingProServices[securityService]!
    : 0;
  const totalPrice = cleaningPrice + securityPrice;

  // Check if services are already included in the bundle list
  const improvementSummaries = bundleList.flatMap((bundle) =>
    bundle.items.map((item) => item?.maintenance?.summary.trim().toLowerCase()),
  );

  const hasCleaningService =
    cleaningService &&
    improvementSummaries.includes(cleaningService.toLowerCase());
  const hasSecurityService =
    securityService &&
    improvementSummaries.includes(securityService.toLowerCase());

  // Determine which services are missing and generate dynamic copy
  const isSpotCleaning =
    cleaningService === CLEANING_PRO_SERVICE.preMoveInSpotClean;
  const isDeepCleaning =
    cleaningService === CLEANING_PRO_SERVICE.preMoveInDeepClean;
  const isRekeying = securityService === 'Rekeying and Key Copying';
  const isRecoding = securityService === 'Keyless Entry Recoding';

  const missingCleaning = !hasCleaningService && cleaningService;
  const missingSecurity = !hasSecurityService && securityService;

  const generateDisclaimerCopy = () => {
    let copy = '';

    // Opening phrase based on what services are missing
    if (missingCleaning && missingSecurity) {
      copy += STRINGS.copyTemplates.openingPhrases.bothServices;
    } else if (missingCleaning) {
      copy += STRINGS.copyTemplates.openingPhrases.cleaningOnly;
    } else if (missingSecurity) {
      copy += STRINGS.copyTemplates.openingPhrases.securityOnly;
    }

    copy += STRINGS.copyTemplates.belongIntro;

    // Cleaning service description
    if (missingCleaning) {
      if (isSpotCleaning) {
        copy += STRINGS.copyTemplates.cleaningDescriptions.spotCleaning;
      } else if (isDeepCleaning) {
        copy += STRINGS.copyTemplates.cleaningDescriptions.deepCleaning;
      }
    }

    // Connector if both services are missing
    if (missingCleaning && missingSecurity) {
      copy += STRINGS.copyTemplates.connectors.bothServices;
    }

    // Security service description
    if (missingSecurity) {
      if (isRekeying) {
        copy += STRINGS.copyTemplates.securityDescriptions.rekeying;
      } else if (isRecoding) {
        copy += STRINGS.copyTemplates.securityDescriptions.recoding;
      }
    } else if (missingCleaning) {
      copy += STRINGS.copyTemplates.connectors.cleaningOnly;
    }

    // Cost and billing information
    const serviceCount = (missingCleaning ? 1 : 0) + (missingSecurity ? 1 : 0);
    const thisThese = serviceCount > 1 ? 'these' : 'this';

    const costText = template(STRINGS.copyTemplates.costTemplate, {
      thisThese,
      totalPrice: formatMoney(totalPrice),
    });
    copy += costText;

    return copy;
  };

  // If both services are already included, don't show disclaimer
  if (hasCleaningService && hasSecurityService) {
    return null;
  }

  if (!missingCleaning && !missingSecurity) {
    return null;
  }

  return (
    <div className="my-2xl">
      <div className="flex flex-col gap-xs rounded border border-solid border-green bg-green-translucent-light p-sm">
        <div className="flex items-center justify-between">
          <Text fontWeight="semibold">
            {STRINGS.rekeyingSpotCleaningDisclaimerTitle}
          </Text>
          <Text fontWeight="semibold">+&nbsp;{formatMoney(totalPrice)}</Text>
        </div>
        <Text variant="p1">{generateDisclaimerCopy()}</Text>
      </div>
    </div>
  );
}
